import React from "react";
import { useRealTimeUsageStats } from "../../hooks/use-real-time-usage-stats";

interface RealTimeUsageStatsProps {
  messageId?: string;
  className?: string;
  enableSubscriptions?: boolean;
  showRecentActivity?: boolean;
  maxRecentActivity?: number;
}

export function RealTimeUsageStats({
  messageId,
  className = "",
  enableSubscriptions = true,
  showRecentActivity = true,
  maxRecentActivity = 5,
}: RealTimeUsageStatsProps): React.ReactElement {
  const { usageStats, loading, error, isRealTimeActive, refreshStats } =
    useRealTimeUsageStats({
      messageId,
      enableSubscriptions,
      maxRecentActivity,
    });

  if (loading) {
    return (
      <div className={`bg-white rounded-lg border p-4 ${className}`}>
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-8 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg border p-4 ${className}`}>
        <div className="text-center">
          <div className="w-12 h-12 bg-red-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Error loading usage stats
          </h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            onClick={refreshStats}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getActivityIcon = (type: "SMS" | "CHAT") => {
    if (type === "SMS") {
      return (
        <svg
          className="w-3 h-3 text-green-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 18h.01M8 21h8a1 1 0 001-1V4a1 1 0 00-1-1H8a1 1 0 00-1 1v16a1 1 0 001 1z"
          />
        </svg>
      );
    }

    return (
      <svg
        className="w-3 h-3 text-blue-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        />
      </svg>
    );
  };

  return (
    <div className={`bg-white rounded-lg border ${className}`}>
      {/* Header */}
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <h3 className="text-lg font-semibold text-gray-900">
              Usage Statistics
            </h3>

            {/* Real-time indicator */}
            {enableSubscriptions && (
              <div className="ml-3 flex items-center">
                <div
                  className={`w-2 h-2 rounded-full mr-1 ${
                    isRealTimeActive
                      ? "bg-green-400 animate-pulse"
                      : "bg-gray-300"
                  }`}
                />
                <span className="text-xs text-gray-500">
                  {isRealTimeActive ? "Live update" : "Real-time"}
                </span>
              </div>
            )}
          </div>

          {/* Last updated and refresh */}
          <div className="flex items-center space-x-3">
            {usageStats.lastUpdated && (
              <div className="text-xs text-gray-400">
                Updated {formatTime(usageStats.lastUpdated)}
              </div>
            )}

            <button
              onClick={refreshStats}
              className="p-1 text-gray-400 hover:text-gray-600"
              title="Refresh statistics"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="p-4">
        {/* Usage metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Total usage */}
          <div
            className={`bg-purple-50 rounded-lg p-4 ${
              isRealTimeActive ? "ring-2 ring-purple-200 animate-pulse" : ""
            }`}
          >
            <div className="flex items-center">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Total Usage</p>
                <p className="text-2xl font-bold text-gray-900">
                  {usageStats.totalUsage}
                </p>
              </div>
            </div>
          </div>

          {/* SMS usage */}
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 18h.01M8 21h8a1 1 0 001-1V4a1 1 0 00-1-1H8a1 1 0 00-1 1v16a1 1 0 001 1z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">SMS Usage</p>
                <p className="text-2xl font-bold text-gray-900">
                  {usageStats.smsUsage}
                </p>
              </div>
            </div>
          </div>

          {/* Chat usage */}
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Chat Usage</p>
                <p className="text-2xl font-bold text-gray-900">
                  {usageStats.chatUsage}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent activity */}
        {showRecentActivity && usageStats.recentActivity.length > 0 && (
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-3">
              Recent Activity
            </h4>
            <div className="space-y-2">
              {usageStats.recentActivity
                .slice(0, maxRecentActivity)
                .map((activity, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                      index === 0 && isRealTimeActive
                        ? "bg-green-50 border border-green-200 animate-pulse"
                        : "bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center">
                      <div className="mr-3">
                        {getActivityIcon(activity.type)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {activity.type} message sent
                        </p>
                        {activity.deviceType && (
                          <p className="text-xs text-gray-500">
                            Device: {activity.deviceType}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatTime(activity.timestamp)}
                    </div>
                  </div>
                ))}
            </div>

            {usageStats.recentActivity.length === 0 && (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-gray-100 rounded-lg mx-auto mb-3 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <p className="text-gray-500">No recent activity</p>
              </div>
            )}
          </div>
        )}

        {/* Real-time info */}
        {enableSubscriptions && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-start">
              <svg
                className="w-4 h-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">Real-time Updates</p>
                <p className="text-xs">
                  Statistics update automatically when the message is used.
                  Recent activity shows the latest {maxRecentActivity}{" "}
                  interactions.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
